'use client';

import { z } from 'zod';
import parsePhoneNumberFromString from 'libphonenumber-js';
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { subscribeToKlaviyoList } from '@/lib/klaviyo/actions';
import { klaviyoIdentify, getAnonymousId } from '@/lib/klaviyo';
import { useTracking } from '@/lib/tracking/hooks';
import { populateWaitlistSheet } from '@/lib/google';

const formSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email address' }),
  phone: z.string().transform((arg, ctx) => {
    const phone = parsePhoneNumberFromString(arg, {
      defaultCountry: 'DE',
      extract: false,
    });

    if (phone && phone.isValid()) {
      return phone.number;
    }

    if (arg === '' || arg === null || arg === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Phone number is required',
      });
      return z.NEVER;
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid phone number',
    });

    return z.NEVER;
  }),
});

type FormSchema = z.infer<typeof formSchema>;

interface UseWaitlistFormOptions {
  formName: string;
  onSuccess?: () => void;
}

export function useWaitlistForm({
  formName,
  onSuccess,
}: UseWaitlistFormOptions) {
  const [loading, setLoading] = React.useState(false);
  const [formStarted, setFormStarted] = React.useState(false);
  const [emailIdentified, setEmailIdentified] = React.useState(false);
  const { track, identify } = useTracking();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
  });

  // Track form started (only once) with enriched context
  const handleFormStarted = () => {
    if (!formStarted) {
      setFormStarted(true);

      // Capture UTM parameters
      const params = new URLSearchParams(window.location.search);
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');

      track({
        eventName: 'form_started',
        properties: {
          form_name: formName,
          page: window.location.pathname,
          page_url: window.location.href,
          referrer: document.referrer || undefined,
          ...(utmSource && { utm_source: utmSource }),
          ...(utmMedium && { utm_medium: utmMedium }),
          ...(utmCampaign && { utm_campaign: utmCampaign }),
        },
      });
    }
  };

  // Identify user in Klaviyo as soon as they enter a valid email
  const handleEmailBlur = () => {
    const email = form.getValues('email');
    const name = form.getValues('name');

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email && emailRegex.test(email) && !emailIdentified) {
      setEmailIdentified(true);

      // Identify in Klaviyo immediately
      const nameParts = name ? name.trim().split(' ') : [];
      klaviyoIdentify(email, {
        $first_name: nameParts[0] || undefined,
        $last_name: nameParts.slice(1).join(' ') || undefined,
        form_type: formName,
        email_entered: true,
      });

      // Track email entered event
      track({
        eventName: 'email_entered',
        properties: {
          form_name: formName,
          has_name: !!name,
        },
      });
    }
  };

  async function onSubmit(data: FormSchema) {
    setLoading(true);

    try {
      // Get anonymous ID for profile merge
      const anonymousId = getAnonymousId();

      // Execute both API calls in parallel
      const [klaviyoResult, sheetsResult] = await Promise.all([
        subscribeToKlaviyoList({
          email: data.email,
          name: data.name,
          phone: data.phone,
          anonymousId: anonymousId || undefined, // For profile merge
        }),
        populateWaitlistSheet({
          name: data.name,
          email: data.email,
          phone: data.phone,
        }),
      ]);

      if (klaviyoResult.success && sheetsResult.success) {
        // STEP 1: Identify user FIRST (before tracking event)
        const nameParts = data.name.trim().split(' ');
        await identify({
          email: data.email,
          firstName: nameParts[0],
          lastName: nameParts.slice(1).join(' ') || undefined,
          phone: data.phone,
          properties: {
            form_completed: formName,
          },
        });

        // STEP 2: Track successful submission
        // Note: This tracks form submission. "Subscribed to List" event will fire
        // only after user clicks DOI confirmation link in email
        await track({
          eventName: 'form_submitted',
          properties: {
            form_name: formName,
            has_phone: !!data.phone,
            submission_success: true,
          },
        });

        toast.success('Please check your email to confirm your subscription!', {
          description:
            "We've sent you a confirmation email. Click the link to complete your subscription.",
          duration: 6000,
        });
        form.reset();

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }
      } else {
        // Track partial failure
        const failedServices = [];
        if (!klaviyoResult.success) failedServices.push('email subscription');
        if (!sheetsResult.success) failedServices.push('waitlist registration');

        track({
          eventName: 'form_submission_failed',
          properties: {
            form_name: formName,
            error_type: 'partial_failure',
            failed_services: failedServices,
            has_email: !!data.email,
          },
        });

        toast.error('Partial submission failure', {
          description: `Failed: ${failedServices.join(', ')}. Please contact support.`,
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);

      // Track complete failure
      track({
        eventName: 'form_submission_failed',
        properties: {
          form_name: formName,
          error_type: 'complete_failure',
          error_message:
            error instanceof Error ? error.message : 'Unknown error',
        },
      });

      toast.error('Form submission failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  return {
    form,
    loading,
    handleFormStarted,
    handleEmailBlur,
    onSubmit,
  };
}
